import { useEffect, useMemo, useRef, useState } from "react";
import { FEATURE_STATUS_PENDING, FEATURE_STATUS_RERUNNING } from "config";
import {
  getProjectFeatureStatus,
  mapProjectFeatureStatusesToFeatureStatus,
  type ProjectFeatureStatusItem,
} from "utils";
import {
  AI_PIPELINE_LONG_RUN_MS,
  AI_PIPELINE_STEP_ACTIVITY_INTERVAL_MS,
  AI_PIPELINE_STEP_INTERVAL_MS,
  getAiPipelineConfig,
  type AiPipelineCatalogEntry,
  type AiPipelineId,
} from "./aiPipelineCatalog";
import { computeAiPipelineProgress } from "./computeAiPipelineProgress";
import { resolveActiveStepIndex } from "./resolveActiveStepIndex";

export function isAiFeatureStatusRunning(status: string | undefined): boolean {
  return status === FEATURE_STATUS_PENDING || status === FEATURE_STATUS_RERUNNING;
}

export type UseAiFeatureRunStateParams = {
  pipeline: AiPipelineId;
  featureStatuses: ProjectFeatureStatusItem[] | undefined;
  projectId: string | number;
  isRunningOverride?: boolean;
  stepIntervalMs?: number;
};

export type UseAiFeatureRunStateResult = {
  config: AiPipelineCatalogEntry;
  isRunning: boolean;
  status: string | undefined;
  activeStepIndex: number;
  activeStepLabel: string;
  activeStepActivityHint: string;
  stepNumber: number;
  stepCount: number;
  estimatedProgressPercent: number;
  elapsedMs: number;
  showLongRunHint: boolean;
};

export function useAiFeatureRunState({
  pipeline,
  featureStatuses,
  projectId,
  isRunningOverride,
  stepIntervalMs = AI_PIPELINE_STEP_INTERVAL_MS,
}: UseAiFeatureRunStateParams): UseAiFeatureRunStateResult {
  const config = getAiPipelineConfig(pipeline);

  const status = useMemo(() => {
    const mapped = mapProjectFeatureStatusesToFeatureStatus(featureStatuses, projectId);
    return getProjectFeatureStatus(mapped, pipeline)?.status;
  }, [featureStatuses, projectId, pipeline]);

  const isRunningFromStatus = isAiFeatureStatusRunning(status);
  const isRunning = isRunningOverride !== undefined ? isRunningOverride : isRunningFromStatus;

  const startedAtRef = useRef<number | null>(null);
  const [elapsedMs, setElapsedMs] = useState(0);

  useEffect(() => {
    if (isRunning) {
      if (startedAtRef.current === null) {
        startedAtRef.current = Date.now();
      }
      return;
    }
    startedAtRef.current = null;
    setElapsedMs(0);
  }, [isRunning]);

  useEffect(() => {
    if (!isRunning) return;

    const tick = () => {
      if (startedAtRef.current !== null) {
        setElapsedMs(Date.now() - startedAtRef.current);
      }
    };

    tick();
    const intervalId = window.setInterval(tick, 1000);
    return () => window.clearInterval(intervalId);
  }, [isRunning]);

  const configuredStepIntervalMs = config.estimatedTotalMs
    ? config.estimatedTotalMs / config.steps.length
    : stepIntervalMs;

  const activeStepIndex = useMemo(() => {
    return resolveActiveStepIndex({
      elapsedMs,
      stepCount: config.steps.length,
      stepIntervalMs: configuredStepIntervalMs,
      stepDurationWeights: config.stepDurationWeights,
    });
  }, [config.steps.length, config.stepDurationWeights, configuredStepIntervalMs, elapsedMs]);

  const showLongRunHint = elapsedMs >= (config.longRunThresholdMs ?? AI_PIPELINE_LONG_RUN_MS);

  const progress = computeAiPipelineProgress({
    stepCount: config.steps.length,
    activeStepIndex,
    elapsedMs,
    stepIntervalMs: configuredStepIntervalMs,
  });

  const activeStepLabel = config.steps[activeStepIndex] ?? config.steps[0] ?? "";
  const stepHints = config.stepActivityHints[activeStepIndex] ?? [];
  const activityHintIndex =
    stepHints.length > 0
      ? Math.floor(elapsedMs / AI_PIPELINE_STEP_ACTIVITY_INTERVAL_MS) % stepHints.length
      : 0;
  const activeStepActivityHint = stepHints[activityHintIndex] ?? "Still working on this step…";

  return {
    config,
    isRunning,
    status,
    activeStepIndex,
    activeStepLabel,
    activeStepActivityHint,
    stepNumber: progress.stepNumber,
    stepCount: progress.stepCount,
    estimatedProgressPercent: progress.estimatedProgressPercent,
    elapsedMs,
    showLongRunHint,
  };
}

export { AI_PIPELINE_STEP_ACTIVITY_INTERVAL_MS };
